import React, { useRef, useState, useCallback, useEffect } from 'react';

/**
 * DrawingCanvas - An SVG overlay for drawing on presentation slides
 * Supports pencil drawing and eraser functionality with real-time sync
 */
const DrawingCanvas = ({
  width,
  height,
  strokes = [],
  activeTool, // 'pencil' | 'eraser' | null
  color = '#000000',
  size = 3,
  userId,
  onStrokeComplete,
  onEraseStroke,
  disabled = false
}) => {
  const svgRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPoints, setCurrentPoints] = useState([]);
  const [currentStrokeId, setCurrentStrokeId] = useState(null);

  // Get coordinates relative to SVG
  const getCoordinates = useCallback((e) => {
    if (!svgRef.current) return null;

    const svg = svgRef.current;
    const rect = svg.getBoundingClientRect();

    // Handle both mouse and touch events
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    // Convert to percentage coordinates (0-100) for consistent rendering across different sizes
    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;

    return { x, y };
  }, []);

  // Start drawing
  const handlePointerDown = useCallback((e) => {
    if (disabled || !activeTool) return;

    if (activeTool === 'eraser') {
      // For eraser, find and erase strokes at this point
      const coords = getCoordinates(e);
      if (!coords) return;

      // Find strokes near this point
      const eraserRadius = 3; // Percentage
      const strokesToErase = strokes.filter(stroke => {
        return stroke.points.some(point => {
          const dx = point.x - coords.x;
          const dy = point.y - coords.y;
          return Math.sqrt(dx * dx + dy * dy) < eraserRadius;
        });
      });

      if (strokesToErase.length > 0 && onEraseStroke) {
        onEraseStroke(strokesToErase.map(s => s.id));
      }
      return;
    }

    // Pencil drawing
    const coords = getCoordinates(e);
    if (!coords) return;

    e.preventDefault();
    setIsDrawing(true);
    setCurrentPoints([coords]);
    setCurrentStrokeId(`${userId}-${Date.now()}`);
  }, [disabled, activeTool, getCoordinates, strokes, onEraseStroke, userId]);

  // Continue drawing
  const handlePointerMove = useCallback((e) => {
    if (!isDrawing || activeTool !== 'pencil') return;

    const coords = getCoordinates(e);
    if (!coords) return;

    e.preventDefault();
    setCurrentPoints(prev => [...prev, coords]);
  }, [isDrawing, activeTool, getCoordinates]);

  // End drawing
  const handlePointerUp = useCallback((e) => {
    if (!isDrawing || activeTool !== 'pencil') {
      setIsDrawing(false);
      return;
    }

    e.preventDefault();
    setIsDrawing(false);

    // Only save if we have at least 2 points
    if (currentPoints.length >= 2 && onStrokeComplete) {
      const stroke = {
        id: currentStrokeId,
        points: currentPoints,
        color,
        size,
        userId
      };
      onStrokeComplete(stroke);
    }

    setCurrentPoints([]);
    setCurrentStrokeId(null);
  }, [isDrawing, activeTool, currentPoints, currentStrokeId, color, size, userId, onStrokeComplete]);

  // Handle eraser drag
  const handleEraserMove = useCallback((e) => {
    if (activeTool !== 'eraser' || disabled) return;

    // Check if mouse button is pressed
    if (e.buttons !== 1) return;

    const coords = getCoordinates(e);
    if (!coords) return;

    const eraserRadius = 3;
    const strokesToErase = strokes.filter(stroke => {
      return stroke.points.some(point => {
        const dx = point.x - coords.x;
        const dy = point.y - coords.y;
        return Math.sqrt(dx * dx + dy * dy) < eraserRadius;
      });
    });

    if (strokesToErase.length > 0 && onEraseStroke) {
      onEraseStroke(strokesToErase.map(s => s.id));
    }
  }, [activeTool, disabled, getCoordinates, strokes, onEraseStroke]);

  // Convert points array to SVG path
  const pointsToPath = (points) => {
    if (!points || points.length < 2) return '';

    let path = `M ${points[0].x} ${points[0].y}`;

    // Use quadratic bezier curves for smooth lines
    for (let i = 1; i < points.length - 1; i++) {
      const xc = (points[i].x + points[i + 1].x) / 2;
      const yc = (points[i].y + points[i + 1].y) / 2;
      path += ` Q ${points[i].x} ${points[i].y}, ${xc} ${yc}`;
    }

    // Add the last point
    if (points.length > 1) {
      const last = points[points.length - 1];
      path += ` L ${last.x} ${last.y}`;
    }

    return path;
  };

  // Prevent default touch behavior to avoid scrolling while drawing
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const preventTouchScroll = (e) => {
      if (activeTool && !disabled) {
        e.preventDefault();
      }
    };

    svg.addEventListener('touchstart', preventTouchScroll, { passive: false });
    svg.addEventListener('touchmove', preventTouchScroll, { passive: false });

    return () => {
      svg.removeEventListener('touchstart', preventTouchScroll);
      svg.removeEventListener('touchmove', preventTouchScroll);
    };
  }, [activeTool, disabled]);

  // Determine cursor style
  const getCursor = () => {
    if (disabled || !activeTool) return 'default';
    if (activeTool === 'pencil') return 'crosshair';
    if (activeTool === 'eraser') return 'pointer';
    return 'default';
  };

  return (
    <svg
      ref={svgRef}
      className="absolute inset-0 z-10"
      width="100%"
      height="100%"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{
        cursor: getCursor(),
        touchAction: activeTool ? 'none' : 'auto',
        pointerEvents: activeTool ? 'auto' : 'none'
      }}
      onMouseDown={handlePointerDown}
      onMouseMove={activeTool === 'eraser' ? handleEraserMove : handlePointerMove}
      onMouseUp={handlePointerUp}
      onMouseLeave={handlePointerUp}
      onTouchStart={handlePointerDown}
      onTouchMove={handlePointerMove}
      onTouchEnd={handlePointerUp}
    >
      {/* Render existing strokes */}
      {strokes.map((stroke) => (
        <path
          key={stroke.id}
          d={pointsToPath(stroke.points)}
          fill="none"
          stroke={stroke.color}
          strokeWidth={stroke.size * 0.3}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      ))}

      {/* Render current stroke being drawn */}
      {isDrawing && currentPoints.length >= 2 && (
        <path
          d={pointsToPath(currentPoints)}
          fill="none"
          stroke={color}
          strokeWidth={size * 0.3}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      )}

      {/* Eraser cursor indicator */}
      {activeTool === 'eraser' && !disabled && (
        <circle
          cx="50"
          cy="50"
          r="3"
          fill="none"
          stroke="#666"
          strokeWidth="0.2"
          strokeDasharray="1,1"
          className="pointer-events-none opacity-0"
        />
      )}
    </svg>
  );
};

export default DrawingCanvas;
